import { useCallback, useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import Button from '@mui/material/Button';
import CircularProgress from '@mui/material/CircularProgress';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogActions from '@mui/material/DialogActions';
import BugReportOutlinedIcon from '@mui/icons-material/BugReportOutlined';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import EditOutlinedIcon from '@mui/icons-material/EditOutlined';
import api from '../../services/axiosInterceptor.js';
import SnackbarEx from '../Helpers/SnackbarEx.jsx';
import FlowPane from './FlowPane.jsx';
import NodeInspector from './NodeInspector.jsx';
import TreeScopeFields, {
    canSubmitScope,
    scopePayload,
    skuObjectsFromTree,
} from './TreeScopeFields.jsx';
import './inspections.css';

export default function TreeBuilder() {
    const { treeId } = useParams();
    const navigate = useNavigate();
    const [tree, setTree] = useState(null);
    const [baseline, setBaseline] = useState(null);
    const [selectedId, setSelectedId] = useState(null);
    const [collapsed, setCollapsed] = useState({});
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [publishing, setPublishing] = useState(false);
    const [deleting, setDeleting] = useState(false);
    const [deleteOpen, setDeleteOpen] = useState(false);
    const [scopeOpen, setScopeOpen] = useState(false);
    const [editScopeType, setEditScopeType] = useState('category');
    const [editCategory, setEditCategory] = useState(null);
    const [editSkus, setEditSkus] = useState([]);
    const [issues, setIssues] = useState(null);
    const [snack, setSnack] = useState({ open: false, message: '', color: 'success' });

    const dirty =
        tree &&
        baseline &&
        (tree.startNodeId !== baseline.startNodeId ||
            JSON.stringify(tree.nodes) !== JSON.stringify(baseline.nodes) ||
            tree.name !== baseline.name ||
            tree.category !== baseline.category ||
            tree.sku !== baseline.sku ||
            JSON.stringify(tree.skus || []) !== JSON.stringify(baseline.skus || []) ||
            tree.scopeType !== baseline.scopeType);

    const load = useCallback(async () => {
        setLoading(true);
        try {
            const { data } = await api.get(`/api/broylesllc/inspection-trees/${treeId}`);
            setTree(data);
            setBaseline(JSON.parse(JSON.stringify(data)));
            setSelectedId(data.startNodeId || Object.keys(data.nodes || {})[0] || null);
            setIssues(null);
        } catch (err) {
            setSnack({ open: true, message: 'Failed to load tree', color: 'error' });
        } finally {
            setLoading(false);
        }
    }, [treeId]);

    useEffect(() => {
        load();
    }, [load]);

    const onChangeNode = (nodeId, nextNode) => {
        setTree((prev) => ({
            ...prev,
            nodes: {
                ...prev.nodes,
                [nodeId]: { ...nextNode, id: nodeId },
            },
        }));
    };

    const newNodeId = (prefix) =>
        `${prefix}_${Date.now().toString(36)}_${Math.random().toString(36).slice(2, 7)}`;

    const addAction = (actionKind = 'productGrade', value = '', extra = {}) => {
        const id = newNodeId('action');
        const v = String(value || '').trim();
        const isSubflow = actionKind === 'subflow';
        const catalogFieldName = String(extra.catalogFieldName || '').trim();
        const titles = {
            productGrade: v || 'Product Grade',
            variant: v || 'Variant',
            listingField: v || 'Reassign SKU',
            catalogField: catalogFieldName || 'Catalog field',
            subflow: 'Continue in wizard',
        };
        setTree((prev) => ({
            ...prev,
            nodes: {
                ...prev.nodes,
                [id]: {
                    id,
                    type: 'action',
                    actionKind,
                    title: titles[actionKind] || 'Action',
                    value: isSubflow ? '' : actionKind === 'catalogField' ? extra.value ?? '' : v,
                    catalogFieldName: actionKind === 'catalogField' ? catalogFieldName : '',
                    subflowTreeId: isSubflow ? v : '',
                    nextNodeId: '',
                    result: {
                        condition: actionKind === 'productGrade' ? v : '',
                        productGrade: actionKind === 'productGrade' ? v : '',
                        status: '',
                        instructions: '',
                        requiresPhotos: false,
                    },
                },
            },
        }));
        setSelectedId(id);
        return id;
    };

    const addResult = (productGrade = '') => addAction('productGrade', productGrade);

    const addQuestion = () => {
        const id = newNodeId('node');
        setTree((prev) => ({
            ...prev,
            nodes: {
                ...prev.nodes,
                [id]: {
                    id,
                    type: 'question',
                    title: 'New question',
                    prompt: '',
                    inputType: 'single_choice',
                    options: [],
                    branches: [],
                },
            },
        }));
        setSelectedId(id);
        return id;
    };

    const linkBranchToNewAction = (questionId, branchIndex, actionKind = 'productGrade', value = '', extra = {}) => {
        const actionId = newNodeId('action');
        const v = String(value || '').trim();
        const isSubflow = actionKind === 'subflow';
        const catalogFieldName = String(extra.catalogFieldName || '').trim();
        const titles = {
            productGrade: v || 'Product Grade',
            variant: v || 'Variant',
            listingField: v || 'Reassign SKU',
            catalogField: catalogFieldName || 'Catalog field',
            subflow: 'Continue in wizard',
        };
        setTree((prev) => {
            const question = prev.nodes[questionId];
            if (!question) return prev;
            const branches = (question.branches || []).map((b, i) =>
                i === branchIndex ? { ...b, nextNodeId: actionId } : b
            );
            return {
                ...prev,
                nodes: {
                    ...prev.nodes,
                    [actionId]: {
                        id: actionId,
                        type: 'action',
                        actionKind,
                        title: titles[actionKind] || 'Action',
                        value: isSubflow ? '' : actionKind === 'catalogField' ? extra.value ?? '' : v,
                        catalogFieldName: actionKind === 'catalogField' ? catalogFieldName : '',
                        subflowTreeId: isSubflow ? v : '',
                        nextNodeId: '',
                        result: {
                            condition: actionKind === 'productGrade' ? v : '',
                            productGrade: actionKind === 'productGrade' ? v : '',
                            status: '',
                            instructions: '',
                            requiresPhotos: false,
                        },
                    },
                    [questionId]: { ...question, branches },
                },
            };
        });
        setSelectedId(actionId);
        return actionId;
    };

    const linkBranchToNewResult = (questionId, branchIndex, productGrade = '') =>
        linkBranchToNewAction(questionId, branchIndex, 'productGrade', productGrade);

    const linkBranchToNewQuestion = (questionId, branchIndex) => {
        const nextId = newNodeId('node');
        setTree((prev) => {
            const question = prev.nodes[questionId];
            if (!question) return prev;
            const branches = (question.branches || []).map((b, i) =>
                i === branchIndex ? { ...b, nextNodeId: nextId } : b
            );
            return {
                ...prev,
                nodes: {
                    ...prev.nodes,
                    [nextId]: {
                        id: nextId,
                        type: 'question',
                        title: 'New question',
                        prompt: '',
                        inputType: 'single_choice',
                        options: [],
                        branches: [],
                    },
                    [questionId]: { ...question, branches },
                },
            };
        });
        setSelectedId(nextId);
        return nextId;
    };

    /** After an action/result, create a new question and continue into it. */
    const linkActionToNewQuestion = (actionId) => {
        const nextId = newNodeId('node');
        setTree((prev) => {
            const action = prev.nodes[actionId];
            if (!action) return prev;
            return {
                ...prev,
                nodes: {
                    ...prev.nodes,
                    [nextId]: {
                        id: nextId,
                        type: 'question',
                        title: 'New question',
                        prompt: '',
                        inputType: 'single_choice',
                        options: [],
                        branches: [],
                    },
                    [actionId]: { ...action, nextNodeId: nextId },
                },
            };
        });
        setSelectedId(nextId);
        return nextId;
    };

    const linkActionToNewAction = (fromActionId, actionKind = 'productGrade', value = '', extra = {}) => {
        const nextId = newNodeId('action');
        const v = String(value || '').trim();
        const isSubflow = actionKind === 'subflow';
        const catalogFieldName = String(extra.catalogFieldName || '').trim();
        const titles = {
            productGrade: v || 'Product Grade',
            variant: v || 'Variant',
            listingField: v || 'Reassign SKU',
            catalogField: catalogFieldName || 'Catalog field',
            subflow: 'Continue in wizard',
        };
        setTree((prev) => {
            const action = prev.nodes[fromActionId];
            if (!action) return prev;
            return {
                ...prev,
                nodes: {
                    ...prev.nodes,
                    [nextId]: {
                        id: nextId,
                        type: 'action',
                        actionKind,
                        title: titles[actionKind] || 'Action',
                        value: isSubflow ? '' : actionKind === 'catalogField' ? extra.value ?? '' : v,
                        catalogFieldName: actionKind === 'catalogField' ? catalogFieldName : '',
                        subflowTreeId: isSubflow ? v : '',
                        nextNodeId: '',
                        result: {
                            condition: actionKind === 'productGrade' ? v : '',
                            productGrade: actionKind === 'productGrade' ? v : '',
                            status: '',
                            instructions: '',
                            requiresPhotos: false,
                        },
                    },
                    [fromActionId]: { ...action, nextNodeId: nextId },
                },
            };
        });
        setSelectedId(nextId);
        return nextId;
    };

    /** Insert a question between questionId/branchIndex and its current next node. */
    const insertQuestionBetween = (questionId, branchIndex) => {
        const midId = newNodeId('node');
        setTree((prev) => {
            const question = prev.nodes[questionId];
            if (!question) return prev;
            const branch = (question.branches || [])[branchIndex];
            if (!branch) return prev;
            const oldNext = branch.nextNodeId || '';
            const branches = (question.branches || []).map((b, i) =>
                i === branchIndex ? { ...b, nextNodeId: midId } : b
            );
            return {
                ...prev,
                nodes: {
                    ...prev.nodes,
                    [midId]: {
                        id: midId,
                        type: 'question',
                        title: 'Inserted question',
                        prompt: '',
                        inputType: 'single_choice',
                        options: [],
                        branches: oldNext
                            ? [{ value: 'Continue', label: 'Continue', nextNodeId: oldNext }]
                            : [],
                    },
                    [questionId]: { ...question, branches },
                },
            };
        });
        setSelectedId(midId);
        return midId;
    };

    const collectReachable = (startNodeId, nodes) => {
        const reachable = new Set();
        const walk = (id, path) => {
            if (!id || !nodes[id] || path.includes(id) || reachable.has(id)) return;
            reachable.add(id);
            const node = nodes[id];
            if (node.type === 'question') {
                for (const branch of node.branches || []) {
                    if (branch?.nextNodeId) walk(branch.nextNodeId, [...path, id]);
                }
            } else if (node.type === 'action' && node.nextNodeId) {
                walk(node.nextNodeId, [...path, id]);
            }
        };
        if (startNodeId) walk(startNodeId, []);
        return reachable;
    };

    const removeUnusedNodes = () => {
        setTree((prev) => {
            if (!prev) return prev;
            const reachable = collectReachable(prev.startNodeId, prev.nodes || {});
            const nodes = {};
            let removed = 0;
            for (const [id, node] of Object.entries(prev.nodes || {})) {
                if (reachable.has(id)) nodes[id] = node;
                else removed += 1;
            }
            if (removed > 0) {
                setSnack({
                    open: true,
                    message: `Removed ${removed} unused node${removed === 1 ? '' : 's'}`,
                    color: 'success',
                });
                setSelectedId((cur) =>
                    cur && nodes[cur] ? cur : prev.startNodeId || Object.keys(nodes)[0] || null
                );
            } else {
                setSnack({
                    open: true,
                    message: 'No unused nodes to remove',
                    color: 'success',
                });
            }
            return { ...prev, nodes };
        });
        setIssues(null);
    };

    const treeSaveBody = (t) => ({
        name: t.name,
        scopeType: t.scopeType,
        category: t.category,
        sku: t.sku,
        skus: t.skus || [],
        startNodeId: t.startNodeId,
        nodes: t.nodes,
    });

    const openScopeEditor = () => {
        if (!tree) return;
        setEditScopeType(tree.scopeType || 'category');
        setEditCategory(tree.category || null);
        setEditSkus(skuObjectsFromTree(tree));
        setScopeOpen(true);
    };

    const applyScopeEdit = () => {
        const selection = { scopeType: editScopeType, category: editCategory, skus: editSkus };
        if (!canSubmitScope(selection)) return;
        const scope = scopePayload(selection);
        setTree((prev) => ({ ...prev, ...scope }));
        setScopeOpen(false);
    };

    const publish = async () => {
        setPublishing(true);
        try {
            // Drop unreachable leftovers before save/publish so they don't block
            let working = tree;
            const reachable = collectReachable(tree.startNodeId, tree.nodes || {});
            const prunedNodes = {};
            let removedCount = 0;
            for (const [id, node] of Object.entries(tree.nodes || {})) {
                if (reachable.has(id)) prunedNodes[id] = node;
                else removedCount += 1;
            }
            if (removedCount > 0) {
                working = { ...tree, nodes: prunedNodes };
                setTree(working);
            }

            let id = working._id;
            if (dirty || removedCount > 0) {
                const { data: saved } = await api.put(`/api/broylesllc/inspection-trees/${working._id}`, treeSaveBody(working));
                id = saved._id;
                if (saved.forkedFrom) {
                    const { data: published } = await api.post(
                        `/api/broylesllc/inspection-trees/${saved._id}/publish`
                    );
                    setIssues(null);
                    setSnack({ open: true, message: 'Published', color: 'success' });
                    navigate(`/inspections/trees/${published._id}`, { replace: true });
                    return;
                }
                setTree(saved);
                setBaseline(JSON.parse(JSON.stringify(saved)));
            }

            const { data } = await api.post(`/api/broylesllc/inspection-trees/${id}/publish`);
            if (data._id && data._id !== id) {
                navigate(`/inspections/trees/${data._id}`, { replace: true });
            } else {
                setTree(data);
                setBaseline(JSON.parse(JSON.stringify(data)));
            }
            setIssues(null);
            setSnack({
                open: true,
                message:
                    removedCount > 0
                        ? `Published (removed ${removedCount} unused node${removedCount === 1 ? '' : 's'})`
                        : 'Published',
                color: 'success',
            });
        } catch (err) {
            const body = err?.response?.data;
            if (body?.issues) setIssues(body);
            setSnack({
                open: true,
                message: body?.message || 'Publish failed',
                color: 'error',
            });
        } finally {
            setPublishing(false);
        }
    };

    const save = async () => {
        if (!tree) return;
        setSaving(true);
        try {
            const { data } = await api.put(`/api/broylesllc/inspection-trees/${tree._id}`, treeSaveBody(tree));
            if (data.forkedFrom) {
                setSnack({
                    open: true,
                    message: `Created draft v${data.version} (prior version has inspection history)`,
                    color: 'info',
                });
                navigate(`/inspections/trees/${data._id}`, { replace: true });
                return;
            }
            setTree(data);
            setBaseline(JSON.parse(JSON.stringify(data)));
            setSnack({ open: true, message: 'Saved', color: 'success' });
        } catch (err) {
            setSnack({
                open: true,
                message: err?.response?.data?.message || 'Save failed',
                color: 'error',
            });
        } finally {
            setSaving(false);
        }
    };

    const checkIssues = async () => {
        try {
            const { data } = await api.post(`/api/broylesllc/inspection-trees/${tree._id}/validate`, {
                startNodeId: tree.startNodeId,
                nodes: tree.nodes,
            });
            setIssues(data);
            if (data.ok) {
                setSnack({ open: true, message: 'No issues found', color: 'success' });
            }
        } catch (err) {
            setSnack({ open: true, message: 'Validation failed', color: 'error' });
        }
    };

    const confirmDelete = async () => {
        if (!tree?._id) return;
        setDeleting(true);
        try {
            await api.delete(`/api/broylesllc/inspection-trees/${tree._id}`);
            navigate('/inspections/trees', { replace: true });
        } catch (err) {
            setSnack({
                open: true,
                message: err?.response?.data?.message || 'Could not delete tree',
                color: 'error',
            });
            setDeleting(false);
            setDeleteOpen(false);
        }
    };

    if (loading || !tree) {
        return (
            <div className="insp-builder" style={{ alignItems: 'center', justifyContent: 'center' }}>
                <CircularProgress />
            </div>
        );
    }

    const skuList = (tree.skus?.length ? tree.skus : [tree.sku]).filter(Boolean);
    const scopeValue =
        tree.scopeType === 'sku'
            ? skuList.length > 1
                ? `${skuList.length} SKUs`
                : skuList[0] || 'None selected'
            : tree.category || 'None selected';

    return (
        <div className="insp-builder">
            <div className="insp-builder-top">
                <div>
                    <button type="button" className="back" onClick={() => navigate('/inspections/trees')}>
                        ← All trees
                    </button>
                    <h1 style={{ margin: 0, fontSize: '1.25rem' }}>{tree.name}</h1>
                    <div className="insp-scope-row">
                        <button
                            type="button"
                            className="insp-scope-edit"
                            onClick={openScopeEditor}
                            title="Change the SKU or AZ Category this tree is bound to"
                        >
                            <span className="insp-scope-edit-kind">
                                {tree.scopeType === 'sku' ? 'SKU' : 'AZ Category'}
                            </span>
                            <span className="insp-scope-edit-value">{scopeValue}</span>
                            <EditOutlinedIcon sx={{ fontSize: 16 }} />
                            Edit
                        </button>
                    </div>
                    <p className="insp-sub" style={{ margin: '6px 0 0' }}>
                        Version {tree.version} · {tree.status}
                        {dirty ? ' · unsaved changes' : ''}
                    </p>
                </div>
                <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                    <Button
                        variant="outlined"
                        color="error"
                        startIcon={<DeleteOutlineIcon />}
                        disabled={deleting}
                        onClick={() => setDeleteOpen(true)}
                    >
                        Delete
                    </Button>
                    <Button
                        variant="outlined"
                        startIcon={<BugReportOutlinedIcon />}
                        onClick={checkIssues}
                    >
                        Check for issues
                    </Button>
                    <Button
                        variant="outlined"
                        disabled={!dirty || saving}
                        onClick={save}
                    >
                        {saving ? 'Saving…' : 'Save'}
                    </Button>
                    <Button
                        variant="contained"
                        disabled={publishing || tree.status === 'archived'}
                        onClick={publish}
                    >
                        {publishing ? 'Publishing…' : 'Publish'}
                    </Button>
                </div>
            </div>

            {issues && !issues.ok && (
                <div className="insp-issues">
                    <strong>{(issues.issues || []).length} issue(s)</strong>
                    {(issues.issues || []).some((i) => i.code === 'orphan') && (
                        <div style={{ margin: '8px 0' }}>
                            <Button
                                size="small"
                                variant="outlined"
                                onClick={removeUnusedNodes}
                            >
                                Remove unused nodes
                            </Button>
                        </div>
                    )}
                    <ul>
                        {(issues.issues || []).map((issue, i) => (
                            <li key={i}>
                                {issue.nodeId ? (
                                    <button
                                        type="button"
                                        className="back"
                                        style={{ display: 'inline' }}
                                        onClick={() => setSelectedId(issue.nodeId)}
                                    >
                                        {issue.message}
                                    </button>
                                ) : (
                                    issue.message
                                )}
                            </li>
                        ))}
                    </ul>
                </div>
            )}

            <div className="insp-builder-panes">
                <FlowPane
                    tree={tree}
                    selectedId={selectedId}
                    onSelect={setSelectedId}
                    collapsed={collapsed}
                    setCollapsed={setCollapsed}
                    onAddResult={() => addResult()}
                    onAddQuestion={addQuestion}
                    onAddAction={addAction}
                />
                <NodeInspector
                    tree={tree}
                    selectedId={selectedId}
                    onChangeNode={onChangeNode}
                    onSelect={setSelectedId}
                    onAddResult={addResult}
                    onAddAction={addAction}
                    onAddQuestion={addQuestion}
                    onLinkBranchToNewResult={linkBranchToNewResult}
                    onLinkBranchToNewAction={linkBranchToNewAction}
                    onLinkBranchToNewQuestion={linkBranchToNewQuestion}
                    onInsertQuestionBetween={insertQuestionBetween}
                    onLinkActionToNewQuestion={linkActionToNewQuestion}
                    onLinkActionToNewAction={linkActionToNewAction}
                />
            </div>

            <Dialog open={deleteOpen} onClose={() => !deleting && setDeleteOpen(false)}>
                <DialogTitle>Delete tree permanently?</DialogTitle>
                <DialogContent>
                    <p style={{ margin: 0 }}>
                        This will permanently delete <strong>{tree.name}</strong> ({tree.status}) and
                        any inspection records for it. This cannot be undone.
                    </p>
                </DialogContent>
                <DialogActions>
                    <Button disabled={deleting} onClick={() => setDeleteOpen(false)}>
                        Cancel
                    </Button>
                    <Button
                        color="error"
                        variant="contained"
                        disabled={deleting}
                        onClick={confirmDelete}
                    >
                        {deleting ? 'Deleting…' : 'Delete permanently'}
                    </Button>
                </DialogActions>
            </Dialog>

            <Dialog open={scopeOpen} onClose={() => setScopeOpen(false)} maxWidth="sm" fullWidth>
                <DialogTitle>Edit SKU / category</DialogTitle>
                <DialogContent>
                    <TreeScopeFields
                        scopeType={editScopeType}
                        onScopeTypeChange={setEditScopeType}
                        category={editCategory}
                        onCategoryChange={setEditCategory}
                        skus={editSkus}
                        onSkusChange={setEditSkus}
                        helperText="Change the AZ Category or SKU this tree is bound to. Save the tree afterward to keep the change."
                    />
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setScopeOpen(false)}>Cancel</Button>
                    <Button
                        variant="contained"
                        disabled={
                            !canSubmitScope({
                                scopeType: editScopeType,
                                category: editCategory,
                                skus: editSkus,
                            })
                        }
                        onClick={applyScopeEdit}
                    >
                        Apply
                    </Button>
                </DialogActions>
            </Dialog>

            <SnackbarEx
                open={snack.open}
                message={snack.message}
                color={snack.color}
                close={() => setSnack((s) => ({ ...s, open: false }))}
            />
        </div>
    );
}
